import { LucideIcon } from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IgTypography } from "@intelligems/ig-theme";
import {
  ScratchCard as ScratchCardType,
  ScratchOfferCard,
  TabEnumFe,
} from "@intelligems/ig-types";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { checkBooleanScope } from "@src/components/LimitAccess/check-scopes";
import { useHasRequiredShopifyScopes } from "@src/components/LimitAccess/hooks";
import { getOfferTypeKey } from "@src/features/sharedExperience/components/offers/utils";
import { useIsUnderOfferLimit } from "@src/features/sharedExperience/hooks/use-is-under-offer-limit";
import { useDialogs } from "@src/hooks/dialog";
import { EXPERIMENT_ROUTES, PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { contactSupport } from "@src/utils/contactSupport";
import {
  mpCheckoutBlockCreationStarted,
  mpExperienceCreated,
  mpOfferCreationStarted,
  mpPostPurchaseCreationStarted,
  mpPostPurchaseFunnelRequestAccessClicked,
} from "@src/utils/mixpanel";
import { getProductCategoryFromModKey } from "@src/utils/mpUtils";

import { ScratchCard } from "./ScratchCard";
import {
  getCheckoutBlockCards,
  getExperimentCards,
  getOfferCards,
  getOverviewCards,
  getPostPurchaseCards,
} from "./scratchCardDefinitions";

interface StartFromScratchCardsProps {
  renderLocation:
    | "offers"
    | "overview"
    | "checkout"
    | "noOffers"
    | "noExperiments"
    | "noCheckout"
    | "postPurchase"
    | "noPostPurchase";
  hideTitle?: boolean;
  wrap?: boolean;
}

export const StartFromScratchCards = ({
  renderLocation,
  hideTitle,
  wrap,
}: StartFromScratchCardsProps) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes([
    "read_checkout_branding_settings",
  ]);
  const shopifyPlan = useConfiguration("shopify_plan")?.value;
  const organization = useGetOrg();
  const { openDialog } = useDialogs();
  const hasPriceModAccess = checkBooleanScope(
    "start_price_experiment",
    organization.data?.org.configurations
  );
  const shouldDuplicateProducts = useConfiguration<boolean>(
    "should_duplicate_products"
  )?.value;
  const hasPostPurchaseEnabled = useConfiguration<boolean>(
    "enable_post_purchase_checkout"
  )?.value;

  const isUnderOfferLimit = useIsUnderOfferLimit();
  const offerCards = getOfferCards(theme);
  const overviewCards = getOverviewCards(
    theme,
    hasPriceModAccess,
    shouldDuplicateProducts
  );
  const checkoutBlockCards = getCheckoutBlockCards(theme);
  const postPurchaseCards = getPostPurchaseCards(theme);
  const experimentCards = getExperimentCards(theme);

  const cards =
    renderLocation === "offers" || renderLocation === "noOffers"
      ? offerCards
      : renderLocation === "checkout" || renderLocation === "noCheckout"
        ? checkoutBlockCards
        : renderLocation === "postPurchase" ||
            renderLocation === "noPostPurchase"
          ? postPurchaseCards
          : renderLocation === "noExperiments"
            ? experimentCards
            : overviewCards;
  const handleClick = (card: ScratchOfferCard | ScratchCardType) => {
    if (renderLocation === "offers" || renderLocation === "noOffers") {
      if (!isUnderOfferLimit) {
        openDialog("offerLimitReached");
        return;
      }

      const offerCard = card as ScratchOfferCard;
      mpOfferCreationStarted(
        getOfferTypeKey({
          discountApplicationType: offerCard.discountType,
          discountApplicationMethod: offerCard.discountMethod,
          unitType: offerCard.unitType,
        })
      );

      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          source: "scratchCards",
          showOfferDialog: true,
          discountType: offerCard.discountType,
          discountMethod: offerCard.discountMethod,
          unitType: offerCard.unitType,
          isCreate: true,
          isEdit: false,
        },
      });
    } else if (
      renderLocation === "checkout" ||
      renderLocation === "noCheckout"
    ) {
      mpCheckoutBlockCreationStarted(card?.checkoutBlockType || "trustBadge");
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          source: "scratchCards",
          showCheckoutBlocksDialog: true,
          checkoutBlockType: card?.checkoutBlockType,
          checkoutBlockTemplate: card?.checkoutBlockTemplate,
          isCreate: true,
          isEdit: false,
          isUpsell: card?.checkoutBlockType === "upsell",
        },
      });
    } else if (
      renderLocation === "postPurchase" ||
      renderLocation === "noPostPurchase"
    ) {
      if ("comingSoon" in card && card.comingSoon) {
        mpPostPurchaseFunnelRequestAccessClicked();
        openDialog("requestFeatureAccessPylon");
      } else {
        mpPostPurchaseCreationStarted();
        void navigate(PERSONALIZATION_ROUTES.Personalization, {
          state: {
            source: "scratchCards",
            showPostPurchaseDialog: true,
            postPurchaseType: card.id,
            isCreate: true,
            isEdit: false,
          },
        });
      }
    } else if (renderLocation === "noExperiments") {
      const experimentCard = card as ScratchCardType;
      const experimentTypeMap: Record<string, string> = {
        price: "pricing",
        shipping: "shipping",
        onsiteEdits: "content/onsiteEdits",
        offer: "personalization/offer",
        checkoutBlock: "checkoutBlock",
        splitUrl: "content/url",
        template: "content/template",
        theme: "content/theme",
        postPurchase: "postPurchase",
      };

      const experimentType = experimentTypeMap[experimentCard.modType];
      if (
        experimentType === "checkoutBlock" &&
        (shopifyPlan === "shopify_plus" || shopifyPlan === "partner_test") &&
        !hasRequiredShopifyScopes
      ) {
        openDialog("missingShopifyScope");
      } else if (experimentType) {
        mpExperienceCreated("test", "test_page", [
          getProductCategoryFromModKey(experimentCard.modType, "experiment"),
        ]);
        void navigate(
          `${EXPERIMENT_ROUTES.Experiment}/?action=new&type=${experimentType}&tab=${TabEnumFe.TEST_GROUPS}`
        );
      }
    } else {
      void navigate(PERSONALIZATION_ROUTES.Personalization, {
        state: {
          source: "scratchCards",
          modToActivate: (card as ScratchCardType).modType,
        },
      });
    }
  };

  const isCheckoutCardAllowed = useMemo(
    () => shopifyPlan === "shopify_plus" || shopifyPlan === "partner_test",
    [shopifyPlan]
  );

  return (
    <Stack marginTop={wrap ? 0 : 8} direction={"column"}>
      {!hideTitle && (
        <IgTypography variant={"h3"} fontWeight={600}>
          Start from scratch
        </IgTypography>
      )}
      <Grid
        container
        direction={"row"}
        wrap={wrap ? "wrap" : "nowrap"}
        marginTop={5}
        columnSpacing={3}
        rowSpacing={3}
      >
        {cards.map(
          (
            card: (ScratchOfferCard | ScratchCardType) & {
              iconComponent: LucideIcon;
              comingSoon?: boolean;
            }
          ) => {
            const cardCopy = {
              ...card,
              ...(card.id === "checkout-blocks-experiment" && {
                tooltipText: (
                  <IgTypography>
                    This feature requires a Shopify Plus plan. If you're on
                    Shopify Plus but don't have access,{" "}
                    <IgTypography
                      sx={{ textDecoration: "underline", cursor: "pointer" }}
                      onClick={() => {
                        contactSupport();
                      }}
                    >
                      contact support.
                    </IgTypography>
                  </IgTypography>
                ),
                disabled: !isCheckoutCardAllowed,
              }),
              ...(card.id === "post-purchase-experiment" && {
                tooltipText: "This feature is coming soon",
                disabled: !hasPostPurchaseEnabled,
              }),
            };
            return (
              <Grid
                key={card.id}
                size={
                  renderLocation === "postPurchase" ||
                  renderLocation === "noPostPurchase"
                    ? 6
                    : 4
                }
              >
                <ScratchCard
                  card={cardCopy}
                  onClick={() => handleClick(card)}
                  renderLocation={renderLocation}
                />
              </Grid>
            );
          }
        )}
      </Grid>
    </Stack>
  );
};
